import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  ClockIcon, 
  TrashIcon,
  EyeIcon,
  PlusIcon,
  FunnelIcon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import BacktestForm from '@/components/Backtesting/BacktestForm';
import BacktestDetailsModal from '@/components/Backtesting/BacktestDetailsModal';
import BacktestComparison from '@/components/Backtesting/BacktestComparison';
import { backtestingApi } from '@/services/api';

interface Backtest {
  id: string;
  strategyName: string;
  strategyType: string;
  symbol: string;
  startDate: string;
  endDate: string;
  initialBalance: number;
  finalBalance: number;
  totalReturn: number;
  sharpeRatio: number;
  maxDrawdown: number;
  winRate: number;
  totalTrades: number;
  status: string;
  createdAt: string;
}

interface BacktestFormData {
  strategyId: string;
  symbol: string;
  startDate: string;
  endDate: string;
  initialBalance: number;
  config?: any;
}

const BacktestingPage: React.FC = () => {
  const [backtests, setBacktests] = useState<Backtest[]>([]);
  const [selectedBacktestId, setSelectedBacktestId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [filters, setFilters] = useState({
    strategyId: '',
    symbol: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  // Load backtests on component mount
  useEffect(() => {
    loadBacktests();
  }, [filters]);

  const loadBacktests = async () => {
    try {
      setIsLoading(true);
      
      // Filter out empty string parameters to avoid validation errors
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );
      
      const response = await backtestingApi.getBacktests(cleanFilters);
      // Response structure: { success: boolean, data: { backtests: [] } }
      const backtestsData = (response as any).data?.backtests || [];
      setBacktests(backtestsData);
    } catch (error) {
      console.error('Failed to load backtests:', error);
      toast.error('Failed to load backtesting history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBacktest = async (formData: BacktestFormData) => {
    try {
      setIsCreating(true);
      toast.loading('Running backtest...', { id: 'backtest-creation' });
      
      const response = await backtestingApi.createBacktest(formData);
      
      // Response structure: { success: boolean, data: { backtest: {...} } }
      const backtestData = (response as any).data?.backtest;
      const totalReturn = backtestData?.results?.totalReturn || backtestData?.totalReturn || 0;
      
      toast.success(`Backtest completed! Return: ${totalReturn.toFixed(2)}%`, {
        id: 'backtest-creation',
        duration: 5000
      });
      
      setShowCreateForm(false);
      await loadBacktests(); // Refresh the list
      
      // Auto-select the new backtest for viewing
      if (backtestData?.id) {
        setTimeout(() => {
          setSelectedBacktestId(backtestData.id);
        }, 500); // Small delay to ensure list is refreshed
      }
      
    } catch (error: any) {
      console.error('Failed to create backtest:', error);
      toast.error(error.message || 'Failed to run backtest', {
        id: 'backtest-creation'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteBacktest = async (backtestId: string) => {
    if (!confirm('Are you sure you want to delete this backtest? This action cannot be undone.')) {
      return;
    }

    try {
      await backtestingApi.deleteBacktest(backtestId);
      toast.success('Backtest deleted successfully');
      
      // Remove from local state
      setBacktests(prev => prev.filter(b => b.id !== backtestId));
      
      // Clear selection if the deleted backtest was selected
      if (selectedBacktestId === backtestId) {
        setSelectedBacktestId(null);
      }
      
    } catch (error: any) {
      console.error('Failed to delete backtest:', error);
      toast.error(error.message || 'Failed to delete backtest');
    }
  };

  const handleToggleComparison = (backtestId: string) => {
    setSelectedForComparison(prev => {
      if (prev.includes(backtestId)) {
        return prev.filter(id => id !== backtestId);
      } else if (prev.length < 5) {
        return [...prev, backtestId];
      } else {
        toast.error('You can compare up to 5 backtests at a time');
        return prev;
      }
    });
  };

  const getReturnColor = (returnValue: number) => {
    if (returnValue > 0) return 'text-success-custom';
    if (returnValue < 0) return 'text-error-custom';
    return 'text-secondary-custom';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-success-custom bg-success-subtle';
      case 'failed': return 'text-error-custom bg-error-subtle';
      case 'running': return 'text-info-custom bg-info-subtle';
      default: return 'text-secondary-custom bg-secondary-custom';
    }
  };

  if (showComparison && selectedForComparison.length > 0) {
    return (
      <BacktestComparison
        backtestIds={selectedForComparison}
        onBack={() => {
          setShowComparison(false);
          setSelectedForComparison([]);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-secondary-custom py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-heading-1 font-bold text-primary-custom">Strategy Backtesting</h1>
              <p className="mt-2 text-body text-secondary-custom">
                Test your trading strategies against historical data to evaluate performance
              </p>
            </div>
            <div className="flex space-x-3">
              {selectedForComparison.length > 1 && (
                <button
                  onClick={() => setShowComparison(true)}
                  className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <ChartBarIcon className="h-5 w-5 mr-2" />
                  Compare ({selectedForComparison.length})
                </button>
              )}
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                New Backtest
              </button>
            </div>
          </div>
        </div>

        {/* Create Backtest Form */}
        {showCreateForm && (
          <div className="mb-8">
            <BacktestForm
              onSubmit={handleCreateBacktest}
              onCancel={() => setShowCreateForm(false)}
              isLoading={isCreating}
            />
          </div>
        )}

        {/* Filters */}
        <div className="card rounded-lg p-6 shadow-sm mb-6">
          <div className="flex items-center space-x-4">
            <FunnelIcon className="h-5 w-5 text-tertiary-custom" />
            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
              <select
                value={filters.symbol}
                onChange={(e) => setFilters(prev => ({ ...prev, symbol: e.target.value }))}
                className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom rounded-lg text-body"
              >
                <option value="">All Symbols</option>
                <option value="BTC/USDT">BTC/USDT</option>
                <option value="ETH/USDT">ETH/USDT</option>
                <option value="ADA/USDT">ADA/USDT</option>
              </select>
              
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom rounded-lg text-body"
              >
                <option value="created_at">Sort by Date</option>
                <option value="total_return">Sort by Return</option>
                <option value="sharpe_ratio">Sort by Sharpe Ratio</option>
                <option value="max_drawdown">Sort by Drawdown</option>
              </select>
              
              <select
                value={filters.sortOrder}
                onChange={(e) => setFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
                className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom rounded-lg text-body"
              >
                <option value="DESC">Descending</option>
                <option value="ASC">Ascending</option>
              </select>
              
              <button
                onClick={loadBacktests}
                className="flex items-center justify-center px-4 py-2 bg-secondary-custom text-secondary-custom rounded-lg hover-bg-secondary-custom transition-colors"
              >
                <ArrowsUpDownIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Backtests List */}
        <div className="card rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-primary-custom">
            <h2 className="text-heading-2 font-semibold text-primary-custom">
              Backtest History ({backtests.length})
            </h2>
          </div>
          
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-body text-secondary-custom">Loading backtests...</p>
            </div>
          ) : backtests.length === 0 ? (
            <div className="p-12 text-center">
              <ChartBarIcon className="mx-auto h-12 w-12 text-tertiary-custom" />
              <h3 className="mt-2 text-body font-medium text-primary-custom">No backtests</h3>
              <p className="mt-1 text-body text-secondary-custom">
                Get started by creating your first backtest.
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Backtest
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-primary-custom">
                <thead className="bg-secondary-custom">
                  <tr>
                    <th className="px-6 py-3 text-left text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedForComparison(backtests.slice(0, 5).map(b => b.id));
                          } else {
                            setSelectedForComparison([]);
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Strategy
                    </th>
                    <th className="px-6 py-3 text-left text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Symbol
                    </th>
                    <th className="px-6 py-3 text-left text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-6 py-3 text-right text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Total Return
                    </th>
                    <th className="px-6 py-3 text-right text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Sharpe Ratio
                    </th>
                    <th className="px-6 py-3 text-right text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Max Drawdown
                    </th>
                    <th className="px-6 py-3 text-right text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Trades
                    </th>
                    <th className="px-6 py-3 text-center text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-caption font-medium text-secondary-custom uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-primary-custom">
                  {backtests.map((backtest) => (
                    <motion.tr
                      key={backtest.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover-bg-secondary-custom transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedForComparison.includes(backtest.id)}
                          onChange={() => handleToggleComparison(backtest.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-body font-medium text-primary-custom">
                            {backtest.strategyName}
                          </div>
                          <div className="text-body text-secondary-custom capitalize">
                            {backtest.strategyType.replace('_', ' ')}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-caption font-medium bg-secondary-custom text-secondary-custom rounded-md">
                          {backtest.symbol}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-body text-secondary-custom">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {new Date(backtest.startDate).toLocaleDateString()} - {new Date(backtest.endDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-right text-body font-medium ${getReturnColor(backtest.totalReturn)}`}>
                        {backtest.totalReturn > 0 ? '+' : ''}{backtest.totalReturn.toFixed(2)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-body text-primary-custom">
                        {backtest.sharpeRatio.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-body text-red-600">
                        -{Math.abs(backtest.maxDrawdown).toFixed(2)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-body text-primary-custom">
                        {backtest.totalTrades}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 py-1 text-caption font-medium rounded-full ${getStatusColor(backtest.status)}`}>
                          {backtest.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-body font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setSelectedBacktestId(backtest.id)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteBacktest(backtest.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        {backtests.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="card rounded-lg p-6 shadow-sm">
              <div className="text-body font-medium text-secondary-custom">Total Backtests</div>
              <div className="mt-1 text-data-large font-semibold text-primary-custom">{backtests.length}</div>
            </div>
            <div className="card rounded-lg p-6 shadow-sm">
              <div className="text-body font-medium text-secondary-custom">Avg Return</div>
              <div className={`mt-1 text-data-large font-semibold ${getReturnColor(backtests.reduce((sum, b) => sum + b.totalReturn, 0) / backtests.length)}`}>
                {(backtests.reduce((sum, b) => sum + b.totalReturn, 0) / backtests.length).toFixed(2)}%
              </div>
            </div>
            <div className="card rounded-lg p-6 shadow-sm">
              <div className="text-body font-medium text-secondary-custom">Avg Sharpe Ratio</div>
              <div className="mt-1 text-data-large font-semibold text-primary-custom">
                {(backtests.reduce((sum, b) => sum + b.sharpeRatio, 0) / backtests.length).toFixed(2)}
              </div>
            </div>
            <div className="card rounded-lg p-6 shadow-sm">
              <div className="text-body font-medium text-secondary-custom">Success Rate</div>
              <div className="mt-1 text-data-large font-semibold text-green-600">
                {((backtests.filter(b => b.totalReturn > 0).length / backtests.length) * 100).toFixed(0)}%
              </div>
            </div>
          </div>
        )}

        {/* Backtest Details Modal */}
        {selectedBacktestId && (
          <BacktestDetailsModal
            backtestId={selectedBacktestId}
            onClose={() => setSelectedBacktestId(null)}
          />
        )}
      </div>
    </div>
  );
};

export default BacktestingPage; 